import { Component, EventEmitter, HostListener, Inject, ViewEncapsulation } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { FuseValidators } from '@fuse/validators';
import { formatDate } from 'app/helpers/formatDate';
import { ExposedListServiceUsersRes } from 'app/interfaces/responses/Exposed/exposedListServiceUsersRes';
import { ListRoleGroupsRes } from 'app/interfaces/responses/List/listRoleGroupsRes';
import { ListRolesRes } from 'app/interfaces/responses/List/listRolesRes';
import { ServiceUsersService } from 'app/services/api/serviceusers/serviceusers.service';
import { TranslationService } from 'app/services/api/translation/translation.service';
import { AlertsService } from 'app/services/system/alerts/alerts.service';
import { DialogGeneral } from '../../../DialogGeneral';

export interface DialogSettingsServiceUsersDetailsData {
    user: ExposedListServiceUsersRes;
    roles: ListRolesRes[];
    roleGroups: ListRoleGroupsRes[];
}

@Component({
    selector: 'dialog-settings-serviceusers-details',
    templateUrl: 'dialog-settings-serviceusers-details.html',
    encapsulation: ViewEncapsulation.None,
})
export class DialogSettingsServiceUsersDetails extends DialogGeneral {
    //#region KEYPRESSES

    // ------------------- Leyfir notanda að vista á ctrl+s -----------------------
    onKeyDown($event): void {
        // Detect platform
        if (navigator.platform.match('Mac')) {
            this.handleMacKeyEvents($event);
        } else {
            this.handleWindowsKeyEvents($event);
        }
    }

    handleMacKeyEvents($event) {
        // MetaKey documentation
        // https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/metaKey
        let charCode = String.fromCharCode($event.which).toLowerCase();
        if ($event.metaKey && charCode === 's') {
            // Action on Cmd + S
            $event.preventDefault();
            this.save();
        }
    }

    handleWindowsKeyEvents($event) {
        let charCode = String.fromCharCode($event.which).toLowerCase();
        if ($event.ctrlKey && charCode === 's') {
            // Action on Ctrl + S
            $event.preventDefault();
            this.save();
        }
    }

    //#endregion

    form: UntypedFormGroup;
    formEnabled: boolean = true;
    onConfirm = new EventEmitter();
    rolesGroupMap = {};
    passwordMinLength: number = 16;

    /**
     * Constructor
     */
    constructor(
        public matDialogRef: MatDialogRef<DialogSettingsServiceUsersDetails>,
        private _formBuilder: UntypedFormBuilder,
        public dialogRef: MatDialogRef<DialogSettingsServiceUsersDetails>,
        public dialog: MatDialog,
        private alertsService: AlertsService,
        private translationService: TranslationService,
        public serviceUsersService: ServiceUsersService,
        @Inject(MAT_DIALOG_DATA)
        public data: DialogSettingsServiceUsersDetailsData
    ) {
        super(dialogRef);
        for (let group of this.data.roleGroups) {
            this.rolesGroupMap[group.roleGroupId] = this.data.roles.filter((role) => role.roleGroupId == group.roleGroupId);
        }
        this.initForm();
    }

    initForm() {
        this.form = this._formBuilder.group(
            {
                newPassword: new FormControl('', [Validators.minLength(this.passwordMinLength), Validators.required]),
                confirmPassword: new FormControl('', [Validators.required]),
            },
            {
                validators: FuseValidators.mustMatch('newPassword', 'confirmPassword'),
            }
        );
        for (const role of this.data.roles) {
            const hasRole = this.data.user.roles.some((x) => x.roleName == role.roleName);
            this.form.addControl(role.roleName, new FormControl({ value: hasRole, disabled: !role.editable }));
        }
        this.preventAccidentalClose();
    }

    formatDate(d) {
        return formatDate(d, false);
    }

    preventAccidentalClose() {
        // Passa að hindra exit ef breytt
        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe((_) => {
            if (this.form.dirty) {
                let cn = confirm(this.ovistadarBreytingar);
                if (cn) {
                    this.close();
                }
            } else {
                this.close();
            }
        });
    }

    revertAllChanges() {
        if (confirm(this.translationService.translate('info_confirm_revert_changes'))) {
            this.form.markAsPristine();
            this.initForm();
        }
    }

    save() {
        if (!this.data.user.username) {
            this.alertsService.changealertHeader(this.translationService.translate('info_error'));
            this.alertsService.changeAlertErrorMessage(this.translationService.translate('error_user_not_registered_database'));
            this.alertsService.changeAlertErrorShow(true);
            return;
        }

        if (this.form.get('newPassword').value != '') {
            this.serviceUsersService.updateServiceUser({
                username: this.data.user.username,
                password: this.form.get('newPassword').value,
                confirmPassword: this.form.get('confirmPassword').value,
            });
        }

        this.formEnabled = false;
        let roles: string[] = [];
        for (let role of this.data.roles) {
            if (this.form.get(role.roleName).value) {
                roles.push(role.roleName);
            }
        }

        this.serviceUsersService
            .updateServiceUserRoles({
                username: this.data.user.username,
                roles: roles,
            })
            .subscribe((success) => {
                this.form.markAsPristine();
                this.formEnabled = true;
                if (success) {
                    this.onConfirm.emit(true);
                    this.close();
                }
            });
    }

    // ------------------------------------------------------------------------------
    ovistadarBreytingar: string = this.translationService.translate('info_leave_form_dialog_message');

    @HostListener('window:keyup.esc') onKeyUp() {
        if (this.form.dirty) {
            let cn = confirm(this.ovistadarBreytingar);
            if (cn) {
                this.close();
            }
        } else {
            this.close();
        }
    }
    @HostListener('window:beforeunload', ['$event']) unloadHandler(event: Event) {
        if (this.form.dirty) {
            event.returnValue = false;
        } else {
            this.close();
        }
    }

    // ------------------------------------------------------------------------------
    // Passar að prompta alert message ef breytingar hafa átt sér stað sem hafa ekki verið vistaðar
    dialogCloseMessage() {
        if (this.form.dirty) {
            let cn = confirm(this.ovistadarBreytingar);
            if (cn) {
                this.close();
            }
        } else {
            this.close();
        }
    }
}
